<script setup>
// NOTICE: KAPTION-INTEGRATION
// embedded-channel.DASHBOARD.3 — iOS / Android tab: that platform's apps and
// push credentials (ported from the former Rumo SDK settings), plus the SDK
// setup snippet with the inbox's Channel ID. embedded-channel.DASHBOARD.6: no
// help center here.
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { KaptionEmbeddedAPI } from 'dashboard/api/kaptionEmbedded';
import {
  EMBEDDED_PLATFORMS,
  androidSnippet,
  embeddedChannelId,
  installationUrl,
  iosSnippet,
  reactNativeSnippet,
} from 'dashboard/helper/kaptionEmbedded';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
  platform: {
    type: String,
    required: true,
    validator: value =>
      [EMBEDDED_PLATFORMS.IOS, EMBEDDED_PLATFORMS.ANDROID].includes(value),
  },
});

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const isIos = computed(() => props.platform === EMBEDDED_PLATFORMS.IOS);

const apps = ref([]);
const loading = ref(false);
const saving = ref(false);
const pendingRemoval = ref(null);
const removeDialog = ref(null);
const relaySecret = ref('');
const relaySecretDialog = ref(null);

const emptyForm = () => ({
  name: '',
  app_identifier: '',
  environment: 'production',
  push_mode: 'managed',
  privacy_mode: true,
  p8: '',
  team_id: '',
  key_id: '',
  project_id: '',
  service_account_json: '',
  relay_url: '',
  relay_secret: '',
});
const form = reactive(emptyForm());

const snippetArgs = computed(() => ({
  channelId: embeddedChannelId(props.inbox),
  baseUrl: installationUrl(props.inbox),
}));
const nativeSnippet = computed(() =>
  isIos.value
    ? iosSnippet(snippetArgs.value)
    : androidSnippet(snippetArgs.value)
);
const rnSnippet = computed(() => reactNativeSnippet(snippetArgs.value));

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(', ');
  return data?.error || data?.message || fallback;
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await KaptionEmbeddedAPI.listApps(
      accountId.value,
      props.inbox.id
    );
    apps.value = (data || []).filter(app => app.platform === props.platform);
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMBEDDED.APPS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const credentials = () => {
  if (form.push_mode === 'relay') return undefined;
  if (isIos.value) {
    return { p8: form.p8, team_id: form.team_id, key_id: form.key_id };
  }
  return {
    project_id: form.project_id,
    service_account_json: form.service_account_json,
  };
};

const appPayload = () => {
  const relay = form.push_mode === 'relay';
  return {
    name: form.name.trim() || form.app_identifier.trim(),
    platform: props.platform,
    app_identifier: form.app_identifier.trim(),
    environment: isIos.value ? form.environment : null,
    push_mode: form.push_mode,
    privacy_mode: form.privacy_mode,
    credentials: credentials(),
    relay_url: relay ? form.relay_url : null,
    relay_secret: relay ? form.relay_secret : null,
  };
};

const createApp = async () => {
  if (!form.app_identifier.trim()) return;
  saving.value = true;
  try {
    await KaptionEmbeddedAPI.createApp(
      accountId.value,
      props.inbox.id,
      appPayload()
    );
    Object.assign(form, emptyForm());
    useAlert(t('INBOX_MGMT.EMBEDDED.APPS.SAVED'));
    await load();
  } catch (error) {
    useAlert(errorMessage(error, t('INBOX_MGMT.EMBEDDED.APPS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
};

const askRemove = app => {
  pendingRemoval.value = app;
  removeDialog.value?.open();
};

const confirmRemove = async () => {
  const app = pendingRemoval.value;
  if (!app) return;
  try {
    await KaptionEmbeddedAPI.deleteApp(accountId.value, props.inbox.id, app.id);
    useAlert(t('INBOX_MGMT.EMBEDDED.APPS.REMOVED'));
    await load();
  } catch (error) {
    useAlert(errorMessage(error, t('INBOX_MGMT.EMBEDDED.APPS.REMOVE_ERROR')));
  } finally {
    pendingRemoval.value = null;
    removeDialog.value?.close();
  }
};

const testPush = async app => {
  try {
    const { data } = await KaptionEmbeddedAPI.testPush(
      accountId.value,
      props.inbox.id,
      app.id
    );
    useAlert(
      data.success
        ? t('INBOX_MGMT.EMBEDDED.APPS.TEST_PUSH_OK')
        : t('INBOX_MGMT.EMBEDDED.APPS.TEST_PUSH_FAILED', {
            error: data.error_code || data.status_code,
          })
    );
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.EMBEDDED.APPS.TEST_PUSH_ERROR'))
    );
  }
};

const rotateRelaySecret = async app => {
  try {
    const { data } = await KaptionEmbeddedAPI.rotateRelaySecret(
      accountId.value,
      props.inbox.id,
      app.id
    );
    relaySecret.value = data.relay_secret;
    relaySecretDialog.value?.open();
  } catch (error) {
    useAlert(
      errorMessage(error, t('INBOX_MGMT.EMBEDDED.APPS.RELAY_SECRET_ERROR'))
    );
  }
};

const closeRelaySecret = () => {
  relaySecret.value = '';
};

onMounted(load);
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.APPS.SETUP_TITLE')"
      :sub-title="t('INBOX_MGMT.EMBEDDED.APPS.SETUP_DESC')"
    >
      <div class="flex flex-col gap-3">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EMBEDDED.APPS.NATIVE') }}
        </p>
        <woot-code
          :script="nativeSnippet"
          :lang="isIos ? 'swift' : 'kotlin'"
          data-testid="native-snippet"
        />
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EMBEDDED.APPS.REACT_NATIVE') }}
        </p>
        <woot-code
          :script="rnSnippet"
          lang="javascript"
          data-testid="rn-snippet"
        />
      </div>
    </SettingsSection>

    <SettingsSection
      :title="
        isIos
          ? t('INBOX_MGMT.EMBEDDED.APPS.IOS_TITLE')
          : t('INBOX_MGMT.EMBEDDED.APPS.ANDROID_TITLE')
      "
      :sub-title="
        isIos
          ? t('INBOX_MGMT.EMBEDDED.APPS.IOS_DESC')
          : t('INBOX_MGMT.EMBEDDED.APPS.ANDROID_DESC')
      "
    >
      <p v-if="loading" class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.EMBEDDED.APPS.LOADING') }}
      </p>
      <p v-else-if="!apps.length" class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.EMBEDDED.APPS.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-3 m-0 list-none">
        <li
          v-for="app in apps"
          :key="app.id"
          class="flex flex-wrap items-start justify-between gap-4 p-4 border rounded-lg border-n-weak bg-n-solid-1"
          data-testid="embedded-app"
        >
          <div class="min-w-0">
            <p class="mb-0 text-sm font-medium text-n-slate-12">
              {{ app.name }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11 break-all">
              {{
                [app.app_identifier, app.push_mode, app.environment]
                  .filter(Boolean)
                  .join(' · ')
              }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.EMBEDDED.APPS.CREDENTIALS') }}
              {{
                app.credentials_configured
                  ? t('INBOX_MGMT.EMBEDDED.APPS.CONFIGURED')
                  : t('INBOX_MGMT.EMBEDDED.APPS.NOT_CONFIGURED')
              }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <NextButton
              sm
              outline
              slate
              :label="t('INBOX_MGMT.EMBEDDED.APPS.TEST_PUSH')"
              @click="testPush(app)"
            />
            <NextButton
              v-if="app.push_mode === 'relay'"
              sm
              outline
              slate
              :label="t('INBOX_MGMT.EMBEDDED.APPS.ROTATE_RELAY_SECRET')"
              @click="rotateRelaySecret(app)"
            />
            <NextButton
              sm
              ghost
              ruby
              :label="t('INBOX_MGMT.EMBEDDED.APPS.REMOVE')"
              @click="askRemove(app)"
            />
          </div>
        </li>
      </ul>
    </SettingsSection>

    <SettingsSection
      :title="t('INBOX_MGMT.EMBEDDED.APPS.ADD_TITLE')"
      :show-border="false"
    >
      <form
        class="flex flex-col gap-4 max-w-xl"
        data-testid="embedded-app-form"
        @submit.prevent="createApp"
      >
        <Input
          v-model="form.name"
          :label="t('INBOX_MGMT.EMBEDDED.APPS.NAME')"
          :placeholder="t('INBOX_MGMT.EMBEDDED.APPS.NAME_PLACEHOLDER')"
        />
        <Input
          v-model="form.app_identifier"
          data-testid="app-identifier"
          :label="
            isIos
              ? t('INBOX_MGMT.EMBEDDED.APPS.BUNDLE_ID')
              : t('INBOX_MGMT.EMBEDDED.APPS.PACKAGE_NAME')
          "
          placeholder="com.example.app"
        />
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ t('INBOX_MGMT.EMBEDDED.APPS.PUSH_MODE') }}
          <select v-model="form.push_mode" class="mb-0">
            <option value="managed">
              {{ t('INBOX_MGMT.EMBEDDED.APPS.MANAGED') }}
            </option>
            <option value="relay">
              {{ t('INBOX_MGMT.EMBEDDED.APPS.RELAY') }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="form.privacy_mode" type="checkbox" />
          {{ t('INBOX_MGMT.EMBEDDED.APPS.CONTENT_FREE') }}
        </label>

        <template v-if="form.push_mode === 'managed' && isIos">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('INBOX_MGMT.EMBEDDED.APPS.APNS_ENVIRONMENT') }}
            <select v-model="form.environment" class="mb-0">
              <option value="production">
                {{ t('INBOX_MGMT.EMBEDDED.APPS.PRODUCTION') }}
              </option>
              <option value="sandbox">
                {{ t('INBOX_MGMT.EMBEDDED.APPS.SANDBOX') }}
              </option>
            </select>
          </label>
          <Input
            v-model="form.team_id"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.APNS_TEAM_ID')"
          />
          <Input
            v-model="form.key_id"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.APNS_KEY_ID')"
          />
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('INBOX_MGMT.EMBEDDED.APPS.APNS_PRIVATE_KEY') }}
            <textarea
              v-model="form.p8"
              rows="5"
              autocomplete="off"
              class="mb-0 font-mono text-xs"
            />
          </label>
        </template>
        <template v-if="form.push_mode === 'managed' && !isIos">
          <Input
            v-model="form.project_id"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.FCM_PROJECT_ID')"
          />
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ t('INBOX_MGMT.EMBEDDED.APPS.FCM_SERVICE_ACCOUNT') }}
            <textarea
              v-model="form.service_account_json"
              rows="7"
              autocomplete="off"
              class="mb-0 font-mono text-xs"
            />
          </label>
        </template>
        <template v-if="form.push_mode === 'relay'">
          <Input
            v-model="form.relay_url"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.RELAY_URL')"
            placeholder="https://"
          />
          <Input
            v-model="form.relay_secret"
            type="password"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.RELAY_SECRET')"
          />
        </template>
        <div>
          <NextButton
            type="submit"
            :disabled="!form.app_identifier.trim()"
            :is-loading="saving"
            :label="t('INBOX_MGMT.EMBEDDED.APPS.SAVE')"
          />
        </div>
      </form>
    </SettingsSection>

    <Dialog
      ref="removeDialog"
      type="alert"
      :title="
        t('INBOX_MGMT.EMBEDDED.APPS.REMOVE_CONFIRM_TITLE', {
          name: pendingRemoval?.name || '',
        })
      "
      :description="t('INBOX_MGMT.EMBEDDED.APPS.REMOVE_CONFIRM_DESC')"
      :confirm-button-label="t('INBOX_MGMT.EMBEDDED.APPS.REMOVE')"
      @confirm="confirmRemove"
    />
    <Dialog
      ref="relaySecretDialog"
      :title="t('INBOX_MGMT.EMBEDDED.APPS.RELAY_SECRET_TITLE')"
      :description="t('INBOX_MGMT.EMBEDDED.APPS.RELAY_SECRET_DESC')"
      :show-confirm-button="false"
      @close="closeRelaySecret"
    >
      <woot-code v-if="relaySecret" :script="relaySecret" lang="plaintext" />
    </Dialog>
  </div>
</template>
